// src/currency.ts
var CURRENCY_CONFIG = {
  GHS: { symbol: "GH\u20B5", locale: "en-GH", decimals: 2 },
  NGN: { symbol: "\u20A6", locale: "en-NG", decimals: 2 },
  KES: { symbol: "KSh", locale: "en-KE", decimals: 2 },
  ZAR: { symbol: "R", locale: "en-ZA", decimals: 2 },
  USD: { symbol: "$", locale: "en-US", decimals: 2 },
  EUR: { symbol: "\u20AC", locale: "de-DE", decimals: 2 },
  GBP: { symbol: "\xA3", locale: "en-GB", decimals: 2 }
};
function formatCurrency(amount, currency = "GHS", options = {}) {
  const { showSymbol = true, compact = false } = options;
  const config = CURRENCY_CONFIG[currency];
  if (compact && Math.abs(amount) >= 1e3) {
    const formatted2 = compactNumber(Math.abs(amount));
    if (!showSymbol) return amount < 0 ? `-${formatted2}` : formatted2;
    return `${amount < 0 ? "-" : ""}${config.symbol}${formatted2}`;
  }
  const numberFormat = new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals
  });
  if (!showSymbol) return numberFormat.format(amount);
  const formatted = numberFormat.format(Math.abs(amount));
  const isNegative = amount < 0 && /[1-9]/.test(formatted);
  return `${isNegative ? "-" : ""}${config.symbol}${formatted}`;
}
function compactNumber(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${abs}`;
}
function calculateCommission(amount, commissionPercent) {
  const commission = Math.round(amount * (commissionPercent / 100) * 100) / 100;
  return { commission, sellerReceives: amount - commission };
}
function parseCurrency(value) {
  return parseFloat(value.replace(/[^0-9.-]/g, ""));
}
function getCurrencySymbol(currency) {
  return CURRENCY_CONFIG[currency].symbol;
}

// src/date.ts
import {
  format,
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
  isAfter,
  isBefore,
  addDays,
  addHours,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  parseISO,
  isValid
} from "date-fns";
function toDate(value) {
  if (value instanceof Date) return value;
  const parsed = parseISO(value);
  if (!isValid(parsed)) throw new Error(`Invalid date string: ${value}`);
  return parsed;
}
function formatDate(value) {
  return format(toDate(value), "d MMM yyyy");
}
function formatDateTime(value) {
  return format(toDate(value), "d MMM yyyy, HH:mm");
}
function formatTime(value) {
  return format(toDate(value), "HH:mm");
}
function timeAgo(value) {
  return formatDistanceToNow(toDate(value), { addSuffix: true });
}
function formatRelative(value) {
  const date = toDate(value);
  const minutesAgo = differenceInMinutes(/* @__PURE__ */ new Date(), date);
  if (minutesAgo < 1) return "Just now";
  if (minutesAgo < 60) return `${minutesAgo}m ago`;
  const hoursAgo = differenceInHours(/* @__PURE__ */ new Date(), date);
  if (hoursAgo < 24) return `${hoursAgo}h ago`;
  const daysAgo = differenceInDays(/* @__PURE__ */ new Date(), date);
  if (daysAgo < 7) return `${daysAgo}d ago`;
  return formatDate(date);
}
function addBusinessDays(date, days) {
  let remaining = days;
  let current = date;
  while (remaining > 0) {
    current = addDays(current, 1);
    if (current.getDay() !== 0 && current.getDay() !== 6) remaining--;
  }
  return current;
}
function calculateDueDate(acceptedAt, deliveryDays) {
  return addDays(toDate(acceptedAt), deliveryDays);
}
function calculateAutoCompleteDate(deliveredAt) {
  return addDays(toDate(deliveredAt), 3);
}
function daysUntil(deadline) {
  return differenceInDays(toDate(deadline), /* @__PURE__ */ new Date());
}
function isExpired(value) {
  return isBefore(toDate(value), /* @__PURE__ */ new Date());
}
function isFuture(value) {
  return isAfter(toDate(value), /* @__PURE__ */ new Date());
}
function formatSeconds(totalSeconds) {
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1e3 });
  return formatDuration(duration, { format: ["hours", "minutes", "seconds"] });
}
function nowISO() {
  return (/* @__PURE__ */ new Date()).toISOString();
}

// src/validation.ts
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_REGEX.test(email.trim().toLowerCase());
}
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
var GH_PHONE_REGEX = /^(\+?233|0)(2[0-9]|5[0-9])\d{7}$/;
function isValidGhanaPhone(phone) {
  const cleaned = phone.replace(/[\s\-()]/g, "");
  return GH_PHONE_REGEX.test(cleaned);
}
function normalizeGhanaPhone(phone) {
  const cleaned = phone.replace(/[\s\-()]/g, "");
  if (cleaned.startsWith("+233")) return cleaned;
  if (cleaned.startsWith("233")) return `+${cleaned}`;
  if (cleaned.startsWith("0")) return `+233${cleaned.slice(1)}`;
  return cleaned;
}
function detectGhanaNetwork(phone) {
  const normalized = normalizeGhanaPhone(phone);
  const prefix = normalized.replace("+233", "0").slice(0, 3);
  if (["024", "054", "055", "059"].includes(prefix)) return "mtn";
  if (["020", "050"].includes(prefix)) return "vodafone";
  if (["026", "027", "056", "057"].includes(prefix)) return "airteltigo";
  if (["023", "028"].includes(prefix)) return "glo";
  return "unknown";
}
function checkPasswordStrength(password) {
  const suggestions = [];
  let score = 0;
  if (password.length >= 8) score++;
  else suggestions.push("Use at least 8 characters");
  if (password.length >= 12) score++;
  else if (password.length < 12) suggestions.push("12+ characters is stronger");
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  else suggestions.push("Mix uppercase and lowercase letters");
  if (/\d/.test(password)) score++;
  else suggestions.push("Add numbers");
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
  else suggestions.push("Add special characters");
  const labels = ["very_weak", "weak", "fair", "strong", "very_strong"];
  return { score: Math.min(score, 4), label: labels[Math.min(score, 4)], suggestions };
}
function isValidPassword(password) {
  return password.length >= 8;
}
var USERNAME_REGEX = /^[a-z0-9_.-]{3,30}$/;
var RESERVED_USERNAMES = ["admin", "cosbyte", "gigship", "support", "help", "api", "www", "mail"];
function isValidUsername(username) {
  const lower = username.toLowerCase();
  if (!USERNAME_REGEX.test(lower)) {
    return { valid: false, reason: "Username must be 3-30 chars: letters, numbers, _ . -" };
  }
  if (RESERVED_USERNAMES.includes(lower)) {
    return { valid: false, reason: "This username is reserved" };
  }
  return { valid: true };
}
function normalizeUsername(username) {
  return username.toLowerCase().trim();
}
var GHANA_CARD_REGEX = /^GHA-\d{9}-\d$/;
function isValidGhanaCardNumber(id) {
  return GHANA_CARD_REGEX.test(id.toUpperCase().trim());
}
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
function isValidAmount(amount, min = 1, max = 1e6) {
  return Number.isFinite(amount) && amount >= min && amount <= max;
}
function sanitizeAmount(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 100) / 100;
}
function isValidRating(rating) {
  return Number.isFinite(rating) && rating >= 1 && rating <= 5;
}

// src/strings.ts
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/--+/g, "-").replace(/^-|-$/g, "");
}
function uniqueSlug(text) {
  const base = slugify(text);
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}
function truncate(text, maxLength, options = {}) {
  const { wordBoundary = true, ellipsis = "..." } = options;
  if (text.length <= maxLength) return text;
  const cutoff = maxLength - ellipsis.length;
  if (!wordBoundary) return text.slice(0, cutoff) + ellipsis;
  const truncated = text.slice(0, cutoff + 1);
  return (truncated.lastIndexOf(" ") > 0 ? truncated.slice(0, truncated.lastIndexOf(" ")) : truncated.slice(0, cutoff)) + ellipsis;
}
function maskEmail(email) {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible = local.slice(0, 2);
  return `${visible}**@${domain}`;
}
function maskPhone(phone) {
  if (phone.length < 6) return phone;
  const last4 = phone.slice(-4);
  return `${phone.slice(0, 4)} **** ${last4}`;
}
function maskAccountNumber(number) {
  if (number.length < 7) return number;
  return `${number.slice(0, 3)}****${number.slice(-3)}`;
}
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
function titleCase(str) {
  return str.replace(/\w\S*/g, capitalize);
}
function camelToTitle(camel) {
  return titleCase(camel.replace(/([A-Z])/g, " $1").trim());
}
function getInitials(name, maxLength = 2) {
  return name.split(" ").filter(Boolean).slice(0, maxLength).map((part) => part[0]?.toUpperCase() ?? "").join("");
}
function generateCode(length = 6) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}
function shortId(length = 7) {
  return Math.random().toString(36).slice(2, 2 + length);
}
function formatNumber(value) {
  return new Intl.NumberFormat("en-GH").format(value);
}
function formatRating(rating) {
  return rating.toFixed(1);
}
function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// src/verification.ts
var VERIFICATION_LEVELS = ["none", "basic", "secondary", "full"];
var VERIFICATION_LEVEL_LABELS = {
  none: "Unverified",
  basic: "Basic Verified",
  secondary: "Secondary Verified",
  full: "Fully Verified"
};
function deriveVerificationLevel(signals) {
  const basic = !!signals.emailVerified && !!signals.phoneVerified;
  if (!basic) return "none";
  const secondary = !!signals.identityVerified || !!signals.hasVerifiedPaymentMethod;
  if (!secondary) return "basic";
  if (!signals.facialVerified) return "secondary";
  return "full";
}
function isAtLeast(level, required) {
  const requiredIdx = VERIFICATION_LEVELS.indexOf(required);
  if (requiredIdx === -1) {
    throw new Error(`Unknown required verification level: ${String(required)}`);
  }
  const levelIdx = VERIFICATION_LEVELS.indexOf(level);
  if (levelIdx === -1) return false;
  return levelIdx >= requiredIdx;
}
var CAPABILITIES = {
  message: "basic",
  gig_post: "basic",
  gig_update: "basic",
  request_post: "basic",
  portfolio_create: "basic",
  custom_offer_create: "secondary",
  custom_request_create: "secondary",
  custom_request_counter: "secondary",
  chat_order_propose: "secondary",
  chat_order_counter: "secondary",
  job_request_apply: "secondary",
  portfolio_request_order: "secondary",
  order_create: "secondary",
  hire: "secondary",
  be_hired: "secondary",
  wallet_topup: "secondary",
  wallet_withdraw: "secondary",
  go_request: "secondary",
  go_accept: "secondary"
};
function canDo(level, capability) {
  const required = CAPABILITIES[capability];
  if (!required) throw new Error(`Unknown verification capability: ${String(capability)}`);
  return isAtLeast(level, required);
}
var DEFAULT_ORDER_CAPS = {
  secondary: { 1: 1e3, 2: 5e3, 3: 1e4 },
  full: { 1: 2500, 2: 7500, 3: 25e3 }
};
function orderCapFor(level, userLevel, caps = DEFAULT_ORDER_CAPS) {
  if (level !== "secondary" && level !== "full") return 0;
  const lvl = userLevel === 2 || userLevel === 3 ? userLevel : 1;
  return caps[level][lvl];
}
export {
  CAPABILITIES,
  DEFAULT_ORDER_CAPS,
  VERIFICATION_LEVELS,
  VERIFICATION_LEVEL_LABELS,
  addBusinessDays,
  addDays,
  addHours,
  calculateAutoCompleteDate,
  calculateCommission,
  calculateDueDate,
  camelToTitle,
  canDo,
  capitalize,
  checkPasswordStrength,
  compactNumber,
  daysUntil,
  deriveVerificationLevel,
  detectGhanaNetwork,
  formatBytes,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatRating,
  formatRelative,
  formatSeconds,
  formatTime,
  generateCode,
  getCurrencySymbol,
  getInitials,
  isAtLeast,
  isExpired,
  isFuture,
  isValidAmount,
  isValidEmail,
  isValidGhanaCardNumber,
  isValidGhanaPhone,
  isValidPassword,
  isValidRating,
  isValidUrl,
  isValidUsername,
  maskAccountNumber,
  maskEmail,
  maskPhone,
  normalizeEmail,
  normalizeGhanaPhone,
  normalizeUsername,
  nowISO,
  orderCapFor,
  parseCurrency,
  sanitizeAmount,
  shortId,
  slugify,
  timeAgo,
  titleCase,
  toDate,
  truncate,
  uniqueSlug
};
